import { Badge, Button, Card, Icon, PageHeader } from "@nelurio/ui";
import { useI18n } from "@nelurio/i18n";
import { useState } from "react";
import { useAdminResource } from "../hooks";
import { ErrorState, LoadingState, healthText, healthTone } from "../components";
import type { SystemStatus } from "../types";

function serviceCard(t: (k: string) => string, name: string, note: string, status: string) {
  return (
    <Card key={name} className="system-card">
      <span className={`system-icon ${healthTone(status) === "green" ? "is-ok" : "is-warn"}`}>
        <Icon name={healthTone(status) === "green" ? "check" : "warning"} />
      </span>
      <div className="system-card__body">
        <strong>{name}</strong>
        <small>{note}</small>
      </div>
      <Badge tone={healthTone(status)}>{healthText(t, status)}</Badge>
    </Card>
  );
}

export function SystemPage() {
  const { t, formatDate, formatNumber } = useI18n();
  const [fresh, setFresh] = useState(0);
  const system = useAdminResource<SystemStatus>(fresh > 0 ? `/system?fresh=${fresh}` : "/system");
  const audit = useAdminResource<Array<{ createdAt: string; admin: string; action: string; targetType: string; targetID: string }>>("/audit?limit=8");
  const data = system.status === "ready" ? system.data : null;
  const recheck = () => setFresh((value) => value + 1);

  const probeCard = (name: string, configured: boolean, probe: { ok: boolean; latencyMs: number; detail?: string } | undefined) => {
    if (!configured) {
      return serviceCard(t, name, t("admin.system.intMissing"), "unknown");
    }
    if (!probe) {
      return serviceCard(t, name, t("admin.system.probePending"), "unknown");
    }
    return (
      <Card key={name} className="system-card">
        <span className={`system-icon ${probe.ok ? "is-ok" : "is-warn"}`}>
          <Icon name={probe.ok ? "check" : "warning"} />
        </span>
        <div className="system-card__body">
          <strong>{name}</strong>
          <small>{probe.detail ? `${probe.detail} · ${formatNumber(probe.latencyMs, { maximumFractionDigits: 0 })} ms` : formatNumber(probe.latencyMs, { maximumFractionDigits: 0 }) + " ms"}</small>
        </div>
        <Badge tone={probe.ok ? "green" : "amber"}>{probe.ok ? t("admin.system.probeOk") : t("admin.system.probeFail")}</Badge>
      </Card>
    );
  };

  return (
    <div className="admin-page">
      <PageHeader
        title={t("admin.system")}
        subtitle={t("admin.system.subtitle")}
        actions={<Button variant="secondary" icon="settings" onClick={recheck}>{t("admin.system.recheck")}</Button>}
      />

      {system.status === "error" && <ErrorState message={system.error} onRetry={() => void system.reload()} />}
      {system.status === "loading" && <LoadingState rows={4} />}

      {system.status === "ready" && data && (
        <div className="system-grid">
          {serviceCard(t, t("admin.system.serviceApi"), t("admin.system.serviceApiNote"), data.api)}
          {serviceCard(
            t,
            t("admin.system.serviceDatabase"),
            data.dbSizeBytes
              ? t("admin.system.serviceDbMeasured", {
                  size: formatNumber(data.dbSizeBytes / (1024 * 1024), { maximumFractionDigits: 1 }),
                  ms: formatNumber(data.dbLatencyMs ?? 0, { maximumFractionDigits: 1 })
                })
              : t("admin.system.serviceDbNote"),
            data.database
          )}
          {serviceCard(
            t,
            t("admin.system.serviceWorker"),
            (data.stalledJobs ?? 0) > 0
              ? t("admin.system.workerStalled", { count: data.stalledJobs })
              : t("admin.system.workerOk"),
            data.worker
          )}
          {/* Live network probes of the external connections. */}
          {probeCard(t("admin.system.intAI"), Boolean(data.integrations?.ai), data.probes?.ai)}
          {probeCard(t("admin.system.smtpConn"), Boolean(data.integrations?.smtp), data.probes?.smtp)}
          {probeCard(t("admin.system.intStorage"), Boolean(data.integrations?.storage), data.probes?.s3)}
          {data.integrations && serviceCard(
            t,
            t("admin.system.pushConn"),
            data.integrations.push ? t("admin.system.pushActive") : t("admin.system.intMissing"),
            data.integrations.push ? "healthy" : "unknown"
          )}
          {/* Real host readings from /proc on the VPS. */}
          {data.host && (
            <Card className="system-card system-card--meta">
              <span className="system-icon is-meta"><Icon name="server" /></span>
              <div className="system-card__body">
                <strong>{t("admin.system.hostTitle")}</strong>
                <small>
                  {t("admin.system.ramRow")}: {formatNumber(data.host.memUsedMb / 1024, { maximumFractionDigits: 1 })} / {formatNumber(data.host.memTotalMb / 1024, { maximumFractionDigits: 1 })} GB ({formatNumber(data.host.memPercent, { maximumFractionDigits: 0 })}%) · {t("admin.system.diskRow")}: {formatNumber(data.host.diskUsedGb, { maximumFractionDigits: 1 })} / {formatNumber(data.host.diskTotalGb, { maximumFractionDigits: 1 })} GB ({formatNumber(data.host.diskPercent, { maximumFractionDigits: 0 })}%)
                </small>
                <small>
                  {t("admin.system.uptimeRow")}: {t("admin.system.daysValue", { days: formatNumber(data.host.uptimeDays, { maximumFractionDigits: 1 }) })} · {t("admin.system.loadRow")}: {formatNumber(data.host.load1, { maximumFractionDigits: 2 })}
                </small>
              </div>
              <Badge tone={data.host.memPercent > 85 || data.host.diskPercent > 85 ? "amber" : "green"}>{formatNumber(Math.max(data.host.memPercent, data.host.diskPercent), { maximumFractionDigits: 0 })}%</Badge>
            </Card>
          )}
          {/* External connections: what is actually wired right now. */}
          {data.integrations && (
            <Card className="system-card system-card--meta">
              <span className="system-icon is-meta"><Icon name="globe" /></span>
              <div className="system-card__body">
                <strong>{t("admin.system.integrationsTitle")}</strong>
                <small>
                  {t("admin.system.intAI")}: {data.integrations.ai ? t("admin.system.intActive") : t("admin.system.intMissing")} · {t("admin.system.intStorage")}: {data.integrations.storage ? t("admin.system.intActive") : t("admin.system.intMissing")} · {t("admin.system.intOAuth")}: {data.integrations.oauth ? t("admin.system.intActive") : t("admin.system.intMissing")}
                </small>
              </div>
              <Badge tone={data.integrations.ai && data.integrations.storage ? "green" : "amber"}>
                {data.integrations.ai && data.integrations.storage ? t("admin.system.intActive") : t("admin.system.intMissing")}
              </Badge>
            </Card>
          )}
          <Card className="system-card system-card--meta">
            <span className="system-icon is-meta"><Icon name="clock" /></span>
            <div className="system-card__body">
              <strong>{t("admin.system.lastCheck")}</strong>
              <small>{t("admin.system.lastCheckNote")}</small>
            </div>
            <Badge tone="neutral">{formatDate(data.time)}</Badge>
          </Card>
        </div>
      )}

      {/* Audit trail: who did what, when (Wix activity-log pattern). */}
      {audit.status === "loading" && <LoadingState rows={2} />}
      {audit.status === "ready" && (audit.data ?? []).length === 0 && (
        <div className="admin-empty-hint">{t("admin.notif.empty")}</div>
      )}
      {audit.status === "ready" && (audit.data ?? []).length > 0 && (
        <Card className="admin-panel admin-audit">
          <h2>{t("admin.system.auditTitle")}</h2>
          <div className="admin-audit__rows">
            {(audit.data ?? []).map((row, index) => (
              <div className="admin-audit__row" key={`${row.createdAt}-${index}`}>
                <code className="admin-audit__action">{row.action}</code>
                <span className="admin-wrap">{row.targetType}{row.targetID ? ` · ${row.targetID.slice(0, 8)}…` : ""}</span>
                <small>{row.admin || "—"}</small>
                <span className="admin-time">{formatDate(row.createdAt)}</span>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
